// apps/widget/src/components/face-overlay.tsx
//
// The oval guide and the guidance ring — green when aligned.
//
// The ring is the whole reason the quality gate feels like help rather than
// rejection: it tells the visitor they are getting closer while they move,
// instead of failing them after the shutter.

import { BAND_COLORS } from '../lib/theme';
import type { FaceGeometry } from '../lib/facemesh';

interface Props {
  geometry: FaceGeometry | null;
  passing: boolean;
}

export function FaceOverlay({ geometry, passing }: Props) {
  const stroke = passing ? BAND_COLORS.good : geometry ? BAND_COLORS.average : 'rgba(255,255,255,.5)';

  return (
    <svg className="gs-overlay" viewBox="0 0 100 130" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <defs>
        {/* The rects reach far outside the viewBox on purpose. With
            preserveAspectRatio="meet" the content is letterboxed inside the
            frame, and a rect sized to the viewBox dimmed only that band —
            leaving the picture bright either side of it. Shapes beyond the
            viewBox still paint, so an oversized rect covers the whole frame
            while the oval keeps its proportions. */}
        <mask id="gs-oval-mask">
          <rect x="-400" y="-400" width="900" height="930" fill="white" />
          <ellipse cx="50" cy="60" rx="30" ry="40" fill="black" />
        </mask>
      </defs>

      {/* Everything outside the oval is dimmed, so the framing target is
          unmistakable without any instruction being read. */}
      <rect
        x="-400"
        y="-400"
        width="900"
        height="930"
        fill="rgba(17,17,17,.55)"
        mask="url(#gs-oval-mask)"
      />

      <ellipse
        cx="50"
        cy="60"
        rx="30"
        ry="40"
        fill="none"
        stroke={stroke}
        strokeWidth={passing ? 1.2 : 0.8}
        strokeDasharray={geometry ? undefined : '3 3'}
        style={{ transition: 'stroke 140ms cubic-bezier(.2,0,0,1), stroke-width 140ms' }}
      />

      {/* A soft pulse the moment all checks pass — the affordance that says
          "now". */}
      {passing && (
        <ellipse cx="50" cy="60" rx="30" ry="40" fill="none" stroke={BAND_COLORS.good} strokeWidth="1.2">
          <animate attributeName="opacity" values="0.9;0.2;0.9" dur="1.6s" repeatCount="indefinite" />
          <animate attributeName="rx" values="30;31.5;30" dur="1.6s" repeatCount="indefinite" />
          <animate attributeName="ry" values="40;41.5;40" dur="1.6s" repeatCount="indefinite" />
        </ellipse>
      )}
    </svg>
  );
}
